import { ReactNode, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';

import { connectivityDashboardActions } from '../../store/reducers/connectivity-dashboard';

import { ReactComponent as AssetConnectedIcon } from '../../assets/connectivitydashboard/monitor-connected-icon.svg';
import { ReactComponent as AssetDisconnectedIcon } from '../../assets/connectivitydashboard/monitor-disconnected-icon.svg';
import { ReactComponent as GatewayConnectedIcon } from '../../assets/connectivitydashboard/gateway-connected-icon.svg';
import { ReactComponent as GatewayDisconnectedIcon } from '../../assets/connectivitydashboard/gateway-disconnected-icon.svg';
import { ReactComponent as EmqxConnectedIcon } from '../../assets/connectivitydashboard/emqx-connected-icon.svg';
import { ReactComponent as EmqxDisconnectedIcon } from '../../assets/connectivitydashboard/emqx-disconnected-icon.svg';
import { ReactComponent as InfluxConnectedIcon } from '../../assets/connectivitydashboard/influx-connected-icon.svg';
import { ReactComponent as InfluxDisconnectedIcon } from '../../assets/connectivitydashboard/influx-disconnected-icon.svg';
import { useTheme } from '@mui/material';

const Styles = {
  wrapper: {
    // mt: 4,
    display: 'flex',
    flexWrap: 'wrap',
    // justifyContent: 'space-between',
    gap: 3,
  },
  card: { p: 3 }, //width: '188px'
  cardContent: {
    p: 0,

    '&:last-child': {
      pb: 0,
    },
  },
  assetsWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    mt: 2,
  },
  asset: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  assetCount: {
    cursor: 'pointer',
    fontWeight: 500,
  },
  assetCountCircle: {
    borderRadius: '50%',
    height: '42px',
    width: '42px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#eceff6',
  },
};

interface ConnectionCardPropTypes {
  label: string;
  loading: boolean | undefined;
  connectedIcon: ReactNode;
  disconnectedIcon: ReactNode;
  connectedData: number | string;
  disconnectedData: number | string;
  onConnectedDataClick: () => void;
  onDisconnectedDataClick: () => void;
}

const ConnectionCard = ({
  label,
  loading,
  connectedIcon,
  disconnectedIcon,
  connectedData,
  disconnectedData,
  onConnectedDataClick,
  onDisconnectedDataClick,
}: ConnectionCardPropTypes) => {
  // const mediaQueryMatches = useMediaQuery('(min-width:1600px)');
  const theme = useTheme();

  return (
    <Grid item xs={6} md={6} lg={3} xl={3}>
      <Card sx={Styles.card}>
        <CardContent sx={Styles.cardContent}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '94px' }}>
              <CircularProgress size={30} />
            </Box>
          ) : (
            <>
              <Typography component='h4' fontSize='16px' color={theme.palette.text.primary}>
                {label}
              </Typography>
              {/* <Box  > */}
              <Grid container sx={Styles.assetsWrapper}>
                <Grid xl={6} lg={6} md={6} sm={6} xs={6} sx={Styles.asset}>
                  {/* <ConnectionIcon /> */}
                  <Box sx={{ ...Styles.assetCountCircle, backgroundColor: '#0EAB4E1A' }}>{connectedIcon}</Box>
                  <Tooltip title='View Details' arrow>
                    <Typography
                      fontSize='36px'
                      color={theme.palette.success.main}
                      sx={Styles.assetCount}
                      onClick={onConnectedDataClick}
                    >
                      {connectedData.toString().padStart(2, '0')}
                    </Typography>
                  </Tooltip>
                </Grid>
                <Grid xl={6} lg={6} md={6} sm={6} xs={6} sx={Styles.asset}>
                  {/* <DisConnectionIcon /> */}
                  <Box sx={{ ...Styles.assetCountCircle, backgroundColor: '#F614141A' }}>{disconnectedIcon}</Box>
                  <Tooltip title='View Details' arrow>
                    <Typography
                      fontSize='36px'
                      color={theme.palette.error.main}
                      sx={Styles.assetCount}
                      onClick={onDisconnectedDataClick}
                    >
                      {disconnectedData.toString().padStart(2, '0')}
                    </Typography>
                  </Tooltip>
                </Grid>
              </Grid>

              {/* </Box> */}
            </>
          )}
        </CardContent>
      </Card>
    </Grid>
  );
};

const Connections = ({
  onConnectionClick,
  assetsCountLoading,
  assetsCount,
  gatewaysCount,
  gatewaysCountLoading,
  emqxInfluxLengthLoading,
  emqxInfluxLength,
}: any) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [assetsLoading, setAssetsLoading] = useState(true);
  const [gatewaysLoading, setGatewaysLoading] = useState(true);
  const [emqxInfluxLoading, setEmqxInfluxLoading] = useState(true);

  useEffect(() => {
    dispatch(connectivityDashboardActions.getEmqxInfluxCountRequest());
  }, []);

  useEffect(() => {
    if (!assetsCountLoading) {
      setAssetsLoading(false);
    }
  }, [assetsCountLoading]);

  useEffect(() => {
    if (!gatewaysCountLoading) {
      setGatewaysLoading(false);
    }
  }, [gatewaysCountLoading]);

  useEffect(() => {
    if (!emqxInfluxLengthLoading) {
      setEmqxInfluxLoading(false);
    }
  }, [emqxInfluxLengthLoading]);

  useEffect(() => {
    let emqxInfluxnterval: any;
    if (!emqxInfluxLengthLoading) {
      emqxInfluxnterval = setInterval(() => {
        dispatch(connectivityDashboardActions.getEmqxInfluxCountRequest());
      }, 60000);
    }

    return () => {
      clearInterval(emqxInfluxnterval);
    };
  }, [emqxInfluxLengthLoading]);

  const onAssetsClick = (item: string, type: string) => {
    onConnectionClick(item, type);
  };

  const onGatewaysClick = (item: string, type: string) => {
    onConnectionClick(item, type);
  };

  const onMQClick = (item: string, type: string) => {
    onConnectionClick(item, type);
  };

  const onInfluxClick = (item: string, type: string) => {
    onConnectionClick(item, type);
  };

  // const onUIServerClick = (item: string, type: string) => {
  //   onConnectionClick(item, type);
  // };

  // const onBackendServerlick = (item: string, type: string) => {
  //   onConnectionClick(item, type);
  // };

  return (
    <Grid container spacing={4}>
      {/* Assets */}
      <ConnectionCard
        label='Assets'
        loading={assetsLoading}
        connectedIcon={<AssetConnectedIcon style={{ color: theme.palette.success.main }} />}
        disconnectedIcon={<AssetDisconnectedIcon style={{ color: theme.palette.error.main }} />}
        connectedData={assetsCount.connected}
        disconnectedData={assetsCount.disconnected}
        onConnectedDataClick={() => onAssetsClick('assets', 'connected')}
        onDisconnectedDataClick={() => onAssetsClick('assets', 'disconnected')}
      />

      {/* Gateways */}
      <ConnectionCard
        label='Gateways'
        loading={gatewaysLoading}
        connectedIcon={<GatewayConnectedIcon style={{ color: theme.palette.success.main }} />}
        disconnectedIcon={<GatewayDisconnectedIcon style={{ color: theme.palette.error.main }} />}
        connectedData={gatewaysCount.connected}
        disconnectedData={gatewaysCount.disconnected}
        onConnectedDataClick={() => onGatewaysClick('gateways', 'connected')}
        onDisconnectedDataClick={() => onGatewaysClick('gateways', 'disconnected')}
      />

      {/* MQ Broker */}
      <ConnectionCard
        label='MQ Broker'
        loading={emqxInfluxLoading}
        connectedIcon={<EmqxConnectedIcon style={{ color: theme.palette.success.main }} />}
        disconnectedIcon={<EmqxDisconnectedIcon style={{ color: theme.palette.error.main }} />}
        connectedData={emqxInfluxLength.connected_emqx_count}
        disconnectedData={emqxInfluxLength.disconnected_emqx_count}
        onConnectedDataClick={() => onMQClick('mqbroker', 'connected')}
        onDisconnectedDataClick={() => onMQClick('mqbroker', 'disconnected')}
      />

      {/* Influx */}
      <ConnectionCard
        label='Influx'
        loading={emqxInfluxLoading}
        connectedIcon={<InfluxConnectedIcon style={{ color: theme.palette.success.main }} />}
        disconnectedIcon={<InfluxDisconnectedIcon style={{ color: theme.palette.error.main }} />}
        connectedData={emqxInfluxLength.connected_influx_count}
        disconnectedData={emqxInfluxLength.disconnected_influx_count}
        onConnectedDataClick={() => onInfluxClick('influx', 'connected')}
        onDisconnectedDataClick={() => onInfluxClick('influx', 'disconnected')}
      />

      {/*{mediaQueryMatches && (*/}
      {/*  <Grid item xl={0.2} display='flex' alignItems='center' justifyContent='center'>*/}
      {/*    <Divider orientation='vertical' sx={{ height: '80%' }} />*/}
      {/*  </Grid>*/}
      {/*)}*/}

      {/* UI Server */}
      {/* <ConnectionCard
        label='UI Server'
        loading={uiServerLengthLoading}
        connectedData={uiServerLength.connected_ui_count}
        disconnectedData={uiServerLength.disconnected_ui_count}
        onConnectedDataClick={() => onUIServerClick('uiserver', 'connected')}
        onDisconnectedDataClick={() => onUIServerClick('uiserver', 'disconnected')}
      /> */}

      {/* Backend Server */}
      {/* <ConnectionCard
        label='Backend Server'
        loading={backendServerLengthLoading}
        connectedData={backendServerLength.connected_backend_count}
        disconnectedData={backendServerLength.disconnected_backend_count}
        onConnectedDataClick={() => onBackendServerlick('backendserver', 'connected')}
        onDisconnectedDataClick={() => onBackendServerlick('backendserver', 'disconnected')}
      /> */}
    </Grid>
  );
};

const mapStateToProps = (state: any) => ({
  assetsCount: state.connectivityDashboard.assetsCount,
  assetsCountLoading: state.connectivityDashboard.assetsLatestLoading,
  gatewaysCount: state.connectivityDashboard.gatewaysCount,
  gatewaysCountLoading: state.connectivityDashboard.gatewaysLatestLoading,
  // mqBrokerLength: state.connectivityDashboard.mqBrokerLength,
  // mqBrokerLengthLoading: state.connectivityDashboard.mqBrokerLengthLoading,
  // influxLength: state.connectivityDashboard.influxLength,
  // influxLengthLoading: state.connectivityDashboard.influxLengthLoading,
  // uiServerLength: state.connectivityDashboard.uiServerLength,
  // uiServerLengthLoading: state.connectivityDashboard.uiServerLengthLoading,
  // backendServerLength: state.connectivityDashboard.backendServerLength,
  // backendServerLengthLoading: state.connectivityDashboard.backendServerLengthLoading,

  emqxInfluxLengthLoading: state.connectivityDashboard.emqxInfluxCountLoading,
  emqxInfluxLength: state.connectivityDashboard.emqxInfluxCount,
});

export default connect(mapStateToProps)(Connections);
